import type { RefObject, MutableRefObject, CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
    PopUpAlignment,
    PopUpCoordinates,
    PopUpSide,
} from "../components";

interface PopUpPosition {
    /** Отвечает за сторону popUp/tooltip, на которой расположен tail */
    side: PopUpSide;
    /** Отвечает за выравнивание tail на выбранной стороне */
    alignment: PopUpAlignment;
    /** Отвечает за отключение перемещения прикрепляемого (PopUp/Tooltip) компонента в видимую часть экрана при частичном скрытии */
    adjustmentLocked: boolean;
    /** Отвечает за ref целевого компонента, к которому привязываем прикрепляемый (PopUp/Tooltip) компонент */
    targetRef: RefObject<HTMLElement>;
    /** Функция, которая вызывается при появлении прикрепляемого (PopUp/Tooltip) компонента */
    onOpen?: () => void;
    /** Функция, которая вызывается при исчезновении прикрепляемого (PopUp/Tooltip) компонента */
    onClose?: () => void;
    /** Функция, которая вызывается перед перерасчетом положения прикрепляемого (PopUp/Tooltip) компонента */
    onBeforePositionRecalculation?: () => void;
    /** Отвечает за текущее состояние отображения прикрепляемого (PopUp/Tooltip) компонента (открыт/закрыт) */
    open?: boolean;
    /** Отвечает за ref прикрепляемого (PopUp/Tooltip) компонента, который будет присоединен к целевому компоненту */
    popUpRef: MutableRefObject<HTMLDivElement | null>;
    /** Callback для логики анимации появления прикрепляемого (PopUp/Tooltip) компонента */
    enterAnimationCallback: (position: PopUpSide) => void;
    /** Callback для логики анимации исчезновения прикрепляемого (PopUp/Tooltip) компонента */
    exitAnimationCallback: (position: PopUpSide) => void;
    /** Отвечает за задержку перерасчета положения прикрепляемого (PopUp/Tooltip) компонента */
    delay?: number;
}

const DEFAULT_DELAY = 50;

const usePopUpPosition = ({
    side,
    alignment,
    adjustmentLocked,
    targetRef,
    onOpen,
    onClose,
    onBeforePositionRecalculation,
    open,
    popUpRef,
    enterAnimationCallback,
    exitAnimationCallback,
    delay = DEFAULT_DELAY,
}: PopUpPosition) => {
    // Дополнительный стейт рендера требуется для корректной отрисовки анимации enter/exit
    const [renderPopUp, setRenderPopUp] = useState<boolean>(!!open);
    // Стейт видимости требуется для корректной работы анимации при корректировке положения поп-апа
    const [visible, setVisible] = useState<boolean>(false);
    const [focused, setFocused] = useState<boolean>(false);
    const [position, setPosition] = useState<PopUpSide>(side);
    const [coordinates, setCoordinates] = useState<PopUpCoordinates | null>(
        null,
    );
    const debounceTimeoutRef = useRef<number | null>(null);

    const calculatePosition = useCallback(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = window.setTimeout(() => {
            const targetElement = targetRef.current;

            if (!targetElement || !popUpRef.current) return undefined;

            onBeforePositionRecalculation?.();

            const targetRect = targetElement.getBoundingClientRect();
            const popUpRect = popUpRef.current.getBoundingClientRect();
            const popUpComputedStyles = window.getComputedStyle(
                popUpRef.current,
            );
            const computedWidth = parseFloat(popUpComputedStyles.width);
            const computedHeight = parseFloat(popUpComputedStyles.height);
            const computedTailPadding = parseFloat(
                popUpComputedStyles.getPropertyValue("--tail-padding"),
            );

            // Учитываем текущее смещение по скроллу страницы
            const scrollTop =
                window.scrollY || document.documentElement.scrollTop;
            const scrollLeft =
                window.scrollX || document.documentElement.scrollLeft;

            let newPosition = position;

            // Получаем размеры сдвига величиной в половину ширины и высоты поп-апа
            const widthOffset = computedWidth / 2;
            const heightOffset = computedHeight / 2;
            // Размер tail для сдвига
            const tailOffset = 9;
            const tailHorizontalOffset = 8;
            // Объявляем переменные сдвига поп-апа от выравнивания tail
            let alignmentWidthOffset = 0;
            let alignmentHeightOffset = 0;

            // Если alignment === "start", сдвигаем в сторону end
            if (alignment === "start") {
                alignmentWidthOffset = widthOffset;
                alignmentHeightOffset = heightOffset;
            }
            // Если alignment === "end", сдвигаем в сторону start
            if (alignment === "end") {
                alignmentWidthOffset = -widthOffset;
                alignmentHeightOffset = -heightOffset;
            }
            // Если alignment === "custom-start", сдвигаем в сторону end, добавляя отступ tailPadding и половину ширины tail
            if (alignment === "custom-start") {
                alignmentWidthOffset =
                    widthOffset - computedTailPadding - tailOffset;
                alignmentHeightOffset =
                    heightOffset - computedTailPadding - tailOffset;
            }
            // Если alignment === "end", сдвигаем в сторону start, добавляя отступ tailPadding и половину ширины tail
            if (alignment === "custom-end") {
                alignmentWidthOffset =
                    -widthOffset + computedTailPadding + tailOffset;
                alignmentHeightOffset =
                    -heightOffset + computedTailPadding + tailOffset;
            }

            // Рассчитываем позицию для всех возможных вариантов
            const positions = {
                top: {
                    top: targetRect.bottom + scrollTop,
                    left:
                        targetRect.left +
                        scrollLeft +
                        targetRect.width / 2 -
                        computedWidth / 2 +
                        alignmentWidthOffset,
                },
                bottom: {
                    top: targetRect.top + scrollTop - computedHeight,
                    left:
                        targetRect.left +
                        scrollLeft +
                        targetRect.width / 2 -
                        computedWidth / 2 +
                        alignmentWidthOffset,
                },
                left: {
                    top:
                        targetRect.top +
                        scrollTop +
                        targetRect.height / 2 -
                        computedHeight / 2 +
                        alignmentHeightOffset,
                    left: targetRect.right + scrollLeft - tailHorizontalOffset,
                },
                right: {
                    top:
                        targetRect.top +
                        scrollTop +
                        targetRect.height / 2 -
                        computedHeight / 2 +
                        alignmentHeightOffset,
                    left:
                        targetRect.left +
                        scrollLeft -
                        computedWidth +
                        tailHorizontalOffset,
                },
            };

            // Если корректировка положения не отключена, рассчитываем положение и перемещение
            if (!adjustmentLocked) {
                const spaceAbove = targetRect.top;
                const spaceBelow = window.innerHeight - targetRect.bottom;
                const spaceLeft = targetRect.left;
                const spaceRight = window.innerWidth - targetRect.right;
                // Если позиция tail = top, то поп-ап находится снизу от таргет-блока, поэтому при достижении нижней
                // границей поп-апа нижней границы окна перемещаем поп-ап на противоположную сторону
                if (
                    position === "top" &&
                    positions.top.top + computedHeight >
                        window.innerHeight + scrollTop &&
                    spaceAbove >= computedHeight
                ) {
                    newPosition = "bottom";
                }
                // Если позиция tail = bottom, то поп-ап находится сверху от таргет-блока, поэтому при достижении
                // верхней границей поп-апа верхней границы окна перемещаем поп-ап на противоположную сторону
                if (
                    position === "bottom" &&
                    popUpRect.top < 0 &&
                    spaceBelow >= computedHeight
                ) {
                    newPosition = "top";
                }
                // Если позиция tail = left, то поп-ап находится справа от таргет-блока, поэтому при достижении правой
                // границей поп-апа правой границы окна перемещаем поп-ап на противоположную сторону
                if (
                    position === "left" &&
                    positions.left.left + computedWidth >
                        window.innerWidth + scrollLeft &&
                    spaceLeft >= computedWidth
                ) {
                    newPosition = "right";
                }
                // Если позиция tail = right, то поп-ап находится слева от таргет-блока, поэтому при достижении левой
                // границей поп-апа левой границы окна перемещаем поп-ап на противоположную сторону
                if (
                    position === "right" &&
                    popUpRect.left < 0 &&
                    spaceRight >= computedWidth
                ) {
                    newPosition = "left";
                }
            }

            setPosition(newPosition);
            setCoordinates(positions[newPosition]);
        }, delay);
    }, [
        targetRef,
        popUpRef,
        position,
        alignment,
        adjustmentLocked,
        onBeforePositionRecalculation,
        delay,
    ]);

    useEffect(() => setPosition(side), [side]);

    useEffect(() => {
        const targetElement = targetRef.current;
        if (!targetElement) return undefined;

        const handleFocus = () => {
            setFocused(true);
        };

        const handleBlur = () => {
            setFocused(false);
        };

        targetElement.addEventListener("focus", handleFocus);
        targetElement.addEventListener("blur", handleBlur);

        return () => {
            targetElement.removeEventListener("focus", handleFocus);
            targetElement.removeEventListener("blur", handleBlur);
        };
    }, [targetRef]);

    useEffect(() => {
        const shouldOpen = open !== undefined ? open : focused; // Приоритет внешнего управления open

        if (shouldOpen) {
            setVisible(false);
            setRenderPopUp(true);

            onOpen?.();

            requestAnimationFrame(() => {
                calculatePosition();
                setVisible(true);
            });

            setTimeout(() => {
                if (!popUpRef.current) return undefined;

                enterAnimationCallback(position);
            }, 0);

            window.addEventListener("resize", calculatePosition);
            window.addEventListener("scroll", calculatePosition, true);

            return () => {
                window.removeEventListener("resize", calculatePosition);
                window.removeEventListener("scroll", calculatePosition, true);
            };
        }

        if (!popUpRef.current) return undefined;

        exitAnimationCallback(position);

        onClose?.();

        return () => {
            window.removeEventListener("resize", calculatePosition);
            window.removeEventListener("scroll", calculatePosition, true);
        };
    }, [
        open,
        focused,
        calculatePosition,
        position,
        onOpen,
        onClose,
        popUpRef,
        exitAnimationCallback,
        enterAnimationCallback,
    ]);

    useEffect(() => {
        if (open || !popUpRef.current) return undefined;

        popUpRef.current.addEventListener(
            "animationend",
            () => {
                onClose?.();
                setVisible(false);
                setRenderPopUp(false);
            },
            { once: true },
        );
    }, [open, onClose, popUpRef]);

    const wrapperStyles = useMemo<CSSProperties>(() => {
        return {
            top: coordinates?.top || 0,
            left: coordinates?.left || 0,
            visibility: (coordinates && visible
                ? "visible"
                : "hidden") as CSSProperties["visibility"],
        };
    }, [coordinates, visible]);

    return {
        position,
        coordinates,
        visible,
        renderPopUp,
        wrapperStyles,
        setPosition,
        setCoordinates,
        setVisible,
        setRenderPopUp,
    };
};

export default usePopUpPosition;
